import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Breadcrumb from '../components/Breadcrumb';
import axios from 'axios';
import { sanitizeHtml } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DynamicPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/pages/public/${slug}`);
      setPage(response.data);
      setError(null);
    } catch (err) {
      setError('Page not found');
      setPage(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">404</h1>
          <p className="text-zinc-400">Page not found</p>
        </div>
      </div>
    );
  }

  const seo = page.seo || {};

  return (
    <>
      <Helmet>
        <title>{seo.title || page.title}</title>
        {seo.description && <meta name="description" content={seo.description} />}
        {seo.keywords && <meta name="keywords" content={seo.keywords} />}
        {seo.robots && <meta name="robots" content={seo.robots} />}
        {seo.canonical_url && <link rel="canonical" href={seo.canonical_url} />}
        {/* Open Graph */}
        {seo.og_title && <meta property="og:title" content={seo.og_title} />}
        {seo.og_description && <meta property="og:description" content={seo.og_description} />}
        {seo.og_image && <meta property="og:image" content={seo.og_image} />}
        {/* Twitter */}
        {seo.twitter_card && <meta name="twitter:card" content={seo.twitter_card} />}
      </Helmet>

      <div className="min-h-screen bg-zinc-800">
        {/* Breadcrumb */}
        <Breadcrumb items={[{ label: page.title, href: null }]} />

        {/* Hero Section */}
        <div className="border-b border-zinc-800 py-12 bg-gradient-to-b from-zinc-900 to-black">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{page.title}</h1>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          <article className="max-w-4xl mx-auto">
            <div
              className="dynamic-page-content prose prose-invert prose-lg max-w-none
                prose-headings:text-white
                prose-a:text-emerald-400
                prose-strong:text-white
                prose-blockquote:border-emerald-500"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.content) }}
            />
            <style>{`
              .dynamic-page-content { color: #d4d4d8; }
              .dynamic-page-content h1, .dynamic-page-content h2, .dynamic-page-content h3 { color: #fff; }
              .dynamic-page-content a { color: #34d399; }
              .dynamic-page-content strong { color: #fff; }
              .dynamic-page-content [style] { color: unset; }
              .dynamic-page-content [style] * { color: inherit; }
            `}</style>
          </article>
        </div>
      </div>
    </>
  );
};

export default DynamicPage;
